import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { CalendarDays, QrCode, Users, TrendingUp } from 'lucide-react';
import { format, parseISO, eachDayOfInterval, subDays } from 'date-fns';

interface Stats {
  totalScans: number;
  thisMonthScans: number;
  activeCampaigns: number;
  uniqueCustomers: number;
}

interface TrendData {
  date: string;
  count: number;
}

interface ChartDataPoint {
  date: string;
  scans: number;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [scanTrends, setScanTrends] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, trendsRes] = await Promise.all([
        apiClient.get<{ success: boolean; data: Stats }>('/store/dashboard'),
        apiClient.get<{ success: boolean; data: TrendData[] }>(`/store/dashboard/scans?period=${period}`),
      ]);

      setStats(statsRes.data.data);

      // Process pre-aggregated scan trends from API
      const trends = trendsRes.data.data;
      const days = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90 : 365;
      const endDate = new Date();
      const startDate = subDays(endDate, days - 1);

      // Create a map of date -> count from API response
      const scanCountByDate: Record<string, number> = {};
      trends.forEach((trend) => {
        const day = format(parseISO(trend.date), 'yyyy-MM-dd');
        scanCountByDate[day] = trend.count;
      });

      // Fill in all dates in range (including days with 0 scans)
      const dateRange = eachDayOfInterval({ start: startDate, end: endDate });
      const chartData = dateRange.map((date) => {
        const dateKey = format(date, 'yyyy-MM-dd');
        return {
          date: format(date, days <= 30 ? 'MMM d' : 'MMM'),
          scans: scanCountByDate[dateKey] || 0,
        };
      });

      // For longer periods, aggregate by week or month
      if (days > 30) {
        const aggregated: ChartDataPoint[] = [];
        const chunkSize = days > 90 ? 30 : 7;
        for (let i = 0; i < chartData.length; i += chunkSize) {
          const chunk = chartData.slice(i, i + chunkSize);
          aggregated.push({
            date: chunk[0].date,
            scans: chunk.reduce((sum, d) => sum + d.scans, 0),
          });
        }
        setScanTrends(aggregated);
      } else {
        setScanTrends(chartData);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-8">My Dashboard</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Scans"
          value={stats?.totalScans || 0}
          subtitle="All time"
          icon={QrCode}
          bgColor="bg-violet-500"
          onClick={() => navigate('/store/qrcodes')}
        />
        <StatCard
          title="This Month"
          value={stats?.thisMonthScans || 0}
          subtitle="Scans"
          icon={TrendingUp}
          bgColor="bg-emerald-500"
          onClick={() => navigate('/store/campaigns')}
        />
        <StatCard
          title="Active Campaigns"
          value={stats?.activeCampaigns || 0}
          subtitle="Currently running"
          icon={CalendarDays}
          bgColor="bg-sky-500"
          onClick={() => navigate('/store/campaigns')}
        />
        <StatCard
          title="Unique Customers"
          value={stats?.uniqueCustomers || 0}
          subtitle="Total entries"
          icon={Users}
          bgColor="bg-blue-500"
          onClick={() => navigate('/store/customers')}
        />
      </div>

      {/* Line Chart - Scan Trends */}
      <div className="card mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">Scan Trends</h2>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="input w-40"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="1y">Last Year</option>
          </select>
        </div>

        {scanTrends.length > 0 && scanTrends.some((d) => d.scans > 0) ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={scanTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tick={{ fill: '#6b7280', fontSize: 12 }}
                interval={period === '7d' ? 0 : 'preserveStartEnd'}
              />
              <YAxis tick={{ fill: '#6b7280' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                }}
              />
              <Line
                type="monotone"
                dataKey="scans"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={{ fill: '#8b5cf6', strokeWidth: 2 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center text-gray-500 py-12">
            <QrCode className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No scans recorded yet</p>
            <p className="text-sm mt-1">Scans will appear here when customers use your QR codes</p>
          </div>
        )}
      </div>

      {/* Bar Chart for monthly comparison if we have data */}
      {scanTrends.length > 0 && scanTrends.some((d) => d.scans > 0) && (
        <div className="card mb-8">
          <h2 className="text-lg font-semibold mb-6">Daily Scans</h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={scanTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tick={{ fill: '#6b7280', fontSize: 12 }}
                interval={period === '7d' ? 0 : 'preserveStartEnd'}
              />
              <YAxis tick={{ fill: '#6b7280' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                }}
              />
              <Bar dataKey="scans" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/store/qrcodes')}
              className="btn-primary w-full"
            >
              View My QR Codes
            </button>
            <button
              onClick={() => navigate('/store/campaigns')}
              className="btn-secondary w-full"
            >
              View My Campaigns
            </button>
            <button
              onClick={() => navigate('/store/customers')}
              className="btn-secondary w-full"
            >
              View Customers
            </button>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Tips</h2>
          <p className="text-sm text-gray-600 mb-4">
            Get the most out of your participation:
          </p>
          <ul className="text-sm text-gray-500 space-y-1">
            <li>• Download and print your QR codes</li>
            <li>• Place them near checkout or entrances</li>
            <li>• Encourage customers to scan for prizes</li>
            <li>• Track your scan progress on this dashboard</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  bgColor,
  onClick,
}: {
  title: string;
  value: number;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  bgColor: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`${bgColor} rounded-xl p-6 text-white hover:opacity-90 transition-opacity cursor-pointer block w-full text-left`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-white/80 mb-1">{title}</p>
          <p className="text-3xl font-bold">{value.toLocaleString()}</p>
          <p className="text-sm text-white/70 mt-1">{subtitle}</p>
        </div>
        <Icon className="w-8 h-8 text-white/80" />
      </div>
    </button>
  );
}
